// PersonaEditor — array-of-persona sub-editor (US-107).
//
// Each persona is { title, desc }. The editor stacks compact cards with
// title input + description textarea. The parent passes an `onAi` callback
// for the section-level "Suggérer" affordance — wired separately by the
// section header (US-113).

import { useT } from '@barel/i18n';
import { Button, Card, FieldAssembly, Input, Textarea } from '@barel/ui';

import { useListEditor } from '@/lib/useListEditor';

export interface PersonaItem {
  title: string;
  desc: string;
}

export interface PersonaEditorProps {
  values: PersonaItem[];
  onChange: (next: PersonaItem[]) => void;
  testId?: string;
}

export function PersonaEditor({
  values,
  onChange,
  testId = 'business-personas',
}: PersonaEditorProps) {
  const t = useT('businesses');
  const tCommon = useT('common');

  const list = useListEditor(values, onChange);

  const update = (idx: number, patch: Partial<PersonaItem>): void => {
    list.updateAt(idx, (p) => ({ ...p, ...patch }));
  };

  const remove = (idx: number): void => {
    list.removeAt(idx);
  };

  const add = (): void => {
    list.add({ title: '', desc: '' });
  };

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      {values.map((persona, idx) => (
        <Card key={idx} padding="md" radius="xl" data-testid={`${testId}-card-${idx}`}>
          <div className="flex flex-col gap-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <FieldAssembly
                  layout="stacked"
                  label={t('personas.titleLabel')}
                  name={`personas.${idx}.title`}
                >
                  <Input
                    type="text"
                    value={persona.title}
                    placeholder={t('personas.titlePlaceholder')}
                    onChange={(event) => update(idx, { title: event.target.value })}
                    data-testid={`${testId}-title-${idx}`}
                  />
                </FieldAssembly>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={t('personas.remove')}
                onClick={() => remove(idx)}
                data-testid={`${testId}-remove-${idx}`}
              >
                {tCommon('actions.remove')}
              </Button>
            </div>
            <FieldAssembly
              layout="stacked"
              label={t('personas.descLabel')}
              name={`personas.${idx}.desc`}
            >
              <Textarea
                value={persona.desc}
                rows={3}
                placeholder={t('personas.descPlaceholder')}
                onChange={(event) => update(idx, { desc: event.target.value })}
                data-testid={`${testId}-desc-${idx}`}
              />
            </FieldAssembly>
          </div>
        </Card>
      ))}
      <Button type="button" variant="ghost" size="sm" onClick={add} data-testid={`${testId}-add`}>
        {t('personas.add')}
      </Button>
    </div>
  );
}
